import React, { useEffect, useState } from 'react'
import { v4 as uuid4 } from "uuid";
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Loader from '../../../../components/loader';
import "../../pre-work-order/pre-work-order.css"
import { SplitPDFStatusEntity } from '../../../../utils/enum';
import { MAX_SCALE, MIN_SCALE } from '../../../pdf-pin-design/pdf-annotation';
import { Document, Page } from 'react-pdf';
import { Minus, Plus } from 'lucide-react';
import { MultiSelect } from 'primereact/multiselect';
import { InputTextarea } from 'primereact/inputtextarea';
import { IsNullOrEmptyArray, IsNullOrUndefined, IsStringNullEmptyOrUndefined } from '../../../../utils/null-check';
import { APIResponseEntity } from '../../../../interface/api-responce';
import { AxiosResponse } from 'axios';
import { toast } from 'react-toastify';
import { toastOptObj } from '../../../../utils/constants';
import { APIToFetchDraftProductionDrawing, APIToSubmitProductionDrawingMapping } from '../../../../service/api-service';
import { ProductionDrawingBasicDetailEntity, ProductionDrawingPDFPagesEntity, ProductionDrawingSolutionDDLEntity, SubmitLinkedProductionDrawingEntity } from '../../../../interface/production-planning/production-drawing.interface';
import { truncateString } from '../../../../service/shared.service';
import useOverrideBrowserZoom from '../../../../hooks/useOverrideBrowserZoom';

type ProductionDrawingFlowStateEntity = {
    sourcePath: string;
    workOrderId: string;
    selectedSolutionIndexes: string[];
};

const ProductionDrawingLink = () => {

    const { id } = useParams();

    const [isLoading, setIsLoading] = useState(false);
    const [selectedLocations, setSelectedLocations] = useState<string[] | []>([]);
    const [selectedNotes, setSelectedNotes] = useState("");

    const [basicDetails, setBasicDetails] = useState<ProductionDrawingBasicDetailEntity | null>(null);

    const [PDFPageList, setPDFPageList] = useState<ProductionDrawingPDFPagesEntity[] | []>([]);

    const [selectedPDF, setSelectedPDF] = useState<ProductionDrawingPDFPagesEntity | null>(null);

    const [solutionDDL, setSolutionDDL] = useState<ProductionDrawingSolutionDDLEntity[] | []>([]);

    const [scale, setScale] = useState(1);

    const [selectedPage, setSelectedPage] = useState(1);

    const navigate = useNavigate();
    const location = useLocation();
    const flowState = location.state as ProductionDrawingFlowStateEntity;

    const zoomIn = () => setScale((prev) => Math.min(prev + 0.1, MAX_SCALE));
    const zoomOut = () => setScale((prev) => Math.max(prev - 0.1, MIN_SCALE));

    const resetZoom = () => setScale(1);

    const ShopDrawingListElement = ({ rowData }: { rowData: any }) => {
        return (
            <div className={`shop-drawing-list cursor-pointer my-2 ${selectedPage === rowData.pageNumber && "sd-selected"} ${rowData.status === SplitPDFStatusEntity.APPROVED && "sd-approved"}`} onClick={() => handleChangePDF(rowData.pageNumber)}>
                <div className="d-flex justify-content-between align-items-center">
                    <div className='d-flex flex-column gap-1'>
                        <span>{rowData.pageName}</span>
                        <span className='status'>{rowData.status || "pending"}</span>
                    </div>
                    <div className='d-flex align-items-center gap-2'>
                        <div className={`sd-dot  ${selectedPage === rowData.pageNumber && "sd-selected-dot"} ${rowData.status === SplitPDFStatusEntity.APPROVED && "sd-approved-dot"}`} />
                        {
                            PDFPageList.length > 1 && (
                                <img src='/assets/images/delete-red-icon.svg' onClick={(e) => { e.stopPropagation(); handleDeletePDPage(rowData.id); }} />
                            )
                        }
                    </div>
                </div>
            </div>);
    }


    async function fetchDraftProductionDrawingRecords() {
        setIsLoading(true);

        const resp: AxiosResponse<APIResponseEntity> = await APIToFetchDraftProductionDrawing(id as string);
        setIsLoading(false);

        if (resp) {
            if (resp && resp.data && resp.status === 200) {
                const axiosResp: APIResponseEntity = resp.data;

                if (axiosResp && axiosResp.data && axiosResp.status && axiosResp.statusCode === 200) {
                    const solutionListWithUUID: ProductionDrawingSolutionDDLEntity[] = (axiosResp.data.result.solutionList || [])
                        .map((item: ProductionDrawingSolutionDDLEntity) => ({ ...item, uuid: uuid4() }));

                    const remapLocationsToUUID = (locations: string[] = []) =>
                        locations.flatMap((solutionId) =>
                            solutionListWithUUID.filter((item) => item.solutionId === solutionId).map((item) => item.uuid));

                    const pdfPagesWithUUID: ProductionDrawingPDFPagesEntity[] = (axiosResp.data.result.PDFPages || [])
                        .map((page: ProductionDrawingPDFPagesEntity) => ({ ...page, locations: remapLocationsToUUID(page.locations) }));

                    setPDFPageList(pdfPagesWithUUID);
                    setSelectedPDF(pdfPagesWithUUID[0]);
                    setSolutionDDL(solutionListWithUUID);
                    setBasicDetails(() => ({
                        orderNumber: axiosResp.data.result.orderNumber,
                        projectName: axiosResp.data.result.projectName,
                        workOrderNumber: axiosResp.data.result.workOrderNumber
                    }));
                    setSelectedPage(pdfPagesWithUUID[0].pageNumber);
                    setSelectedNotes(pdfPagesWithUUID[0].notes || "");
                    setSelectedLocations(pdfPagesWithUUID[0].locations || []);
                }
            }
            else {
                toast.error(resp.data.message, { ...toastOptObj });
                navigate(-1);
            }
        }
        setIsLoading(false);
    }


    const handleFetchSelectedSolutions = () => {
        if (!selectedPDF) return []
        return solutionDDL.filter((item) => (selectedPDF.locations as string[]).includes(item.uuid));
    }

    const handleGetConfirmedCount = () => {
        return PDFPageList.filter((page: any) => page.status === SplitPDFStatusEntity.APPROVED).length;
    }

    const LinkedSolutionList = ({ item }: { item: ProductionDrawingSolutionDDLEntity }) => {
        return (
            <div className='sd-saved-location-list d-flex flex-column'>
                <span>{item.solutionTitle}</span>
                <span>{selectedPDF ? selectedPDF.notes : ""}</span>
            </div>
        )
    }

    const selectPDFPage = (pageNumber: number, pageList: ProductionDrawingPDFPagesEntity[] = PDFPageList) => {
        const selectedPDF = pageList.find((page) => page.pageNumber === pageNumber);
        if (selectedPDF) {
            setSelectedPDF(selectedPDF);
            setSelectedPage(pageNumber);
            setSelectedNotes(selectedPDF.notes || "");
            setSelectedLocations(selectedPDF.locations || []);
            resetZoom();
        }
    }

    const handleChangePDF = (pageNumber: number) => {
        selectPDFPage(pageNumber);
    }

    const handleDeletePDPage = (id: string) => {
        const updatePDFPageList = PDFPageList.filter((item) => item.id !== id);

        if (updatePDFPageList.length === 0) return;

        if (selectedPDF?.id === id) {
            const newPendingRecordIndex = updatePDFPageList.findIndex((item) => item.status === SplitPDFStatusEntity.PENDING);
            if (newPendingRecordIndex !== -1) {
                setSelectedPDF(updatePDFPageList[newPendingRecordIndex]);
                setSelectedPage(updatePDFPageList[newPendingRecordIndex].pageNumber);
                setSelectedNotes(updatePDFPageList[newPendingRecordIndex].notes);
                setSelectedLocations(updatePDFPageList[newPendingRecordIndex].locations);
            } else {
                setSelectedPDF(updatePDFPageList[0]);
                setSelectedPage(updatePDFPageList[0].pageNumber);
                setSelectedNotes(updatePDFPageList[0].notes);
                setSelectedLocations(updatePDFPageList[0].locations);
            }
        }

        setPDFPageList(updatePDFPageList);
    }

    const handleSaveLinkedSolutionAndNote = () => {
        let updatePDFList = PDFPageList.map((item) => {
            if (item.pageNumber === selectedPage) {
                item.locations = selectedLocations;
                item.notes = selectedNotes;
                item.status = SplitPDFStatusEntity.APPROVED;
            }
            return item;
        });

        const findSelectedPDF = updatePDFList.find((item) => item.pageNumber === selectedPage);
        if (findSelectedPDF && !IsNullOrUndefined(findSelectedPDF)) {
            setSelectedPDF(findSelectedPDF);
        }
        let findPendingRecord = updatePDFList.find((item) => IsStringNullEmptyOrUndefined(item.status) || item.status === SplitPDFStatusEntity.PENDING);
        setPDFPageList(() => updatePDFList);
        if (findPendingRecord) {
            selectPDFPage(findPendingRecord.pageNumber, updatePDFList);
        }
    }

    const handleSubmitLinked = async () => {
        setIsLoading(true);

        const solutionIds = solutionDDL.map((item) => item.uuid);
        const selectedSolutionsIds = PDFPageList.flatMap((item) => item.locations);

        const isCheck = solutionIds.every((item) => selectedSolutionsIds.includes(item));

        if (!isCheck) {
            toast.error("All solutions must be linked to the production drawing!", { ...toastOptObj });
            setIsLoading(false);
            return;
        }

        const obj: SubmitLinkedProductionDrawingEntity = {
            productionDrawingId: id as string,
            mapping: PDFPageList.map((item) => ({
                singlePDFPagesId: item.id,
                notes: item.notes,
                locations: solutionDDL
                    .filter((solution) => (item.locations as string[]).includes(solution.uuid))
                    .map((solution) => ({
                        solutionId: solution.solutionId,
                        solutionType: solution.solutionType
                    }))
            }))
        };

        const resp: AxiosResponse<APIResponseEntity> = await APIToSubmitProductionDrawingMapping(obj);

        if (!resp || resp.status !== 200 || !resp.data) {
            setIsLoading(false);
            return;
        }

        if (!resp.data.status || resp.data.statusCode !== 200) {
            toast.error(resp.data.message, { ...toastOptObj });
            setIsLoading(false);
            return;
        }

        toast.success(resp.data.message, { ...toastOptObj });
        navigate(`/production-drawing-preview/${id}`, { replace: true, state: flowState });
        setIsLoading(false);
    }


    useEffect(() => {
        fetchDraftProductionDrawingRecords();
    }, [])

    useOverrideBrowserZoom(zoomIn, zoomOut, resetZoom);

    return (
        <>
            <Loader isLoading={isLoading} />
            <div className="d-flex flex-column h-100 ">
                {/* Header Section Start */}
                <div className="shop-drawing-header-menu d-flex align-items-center gap-2">
                    <div className="shop-drawing-header-sub flex-grow-1">
                        <div style={{ fontSize: "14px" }} dangerouslySetInnerHTML={{ __html: (basicDetails && !IsNullOrUndefined(basicDetails) ? `<b b style="padding-right:5px;">${basicDetails.projectName || ""}</b>${basicDetails.orderNumber ? `(<b style='font-size:12px'>Order No:</b><span style="font-size:12px"> ${basicDetails.orderNumber || ""}</span>)` : ""}` : "") }} />
                    </div>
                </div>
                {/* Header Section End */}
                <div className="flex-shrink-1 h-100 overflow-auto ">
                    <div className="d-flex flex-row h-100 ">
                        {/* Shop Drawing List section Start */}
                        <div className={`shop-drawing-slide-menu d-flex flex-column h-100`}>
                            <div className='flex-grow-1 ' style={{ maxHeight: "100%", overflowY: "auto" }}>
                                <p className='mb-3'>{handleGetConfirmedCount()} of {PDFPageList.length} confirmed</p>
                                {/* Shop Drawing Listing */}
                                {PDFPageList.map((item, index) => (
                                    <ShopDrawingListElement key={index} rowData={item} />
                                ))}
                            </div>

                        </div>
                        {/* Shop Drawing List section End*/}
                        {/* PDF viewer Start */}
                        <div className="flex-grow-1 overflow-auto py-4  pdf-viewer" style={{ backgroundColor: "#cccccc", position: "relative" }} >
                            <div className='d-flex '>
                                <div className='mx-auto'>
                                    <Document
                                        file={selectedPDF ? selectedPDF.pageURL : ""}
                                    >
                                        <Page
                                            pageNumber={1}
                                            scale={scale}
                                            renderTextLayer={false}
                                            renderAnnotationLayer={false}
                                            className="shadow-sm pdf-page"
                                        />
                                    </Document>
                                </div>
                                <div style={{ position: "fixed", bottom: "3vh", right: "25rem" }}>
                                    <div className='sd-zoom-controller d-flex align-items-center gap-2 justify-content-between'>
                                        <button className='sd-icon-btn' onClick={zoomOut}>
                                            <Minus />
                                        </button>
                                        <span
                                            className={scale <= MIN_SCALE || scale >= MAX_SCALE ? "cursor-pointer text-primary fw-bold" : ""}
                                            onClick={(scale <= MIN_SCALE || scale >= MAX_SCALE) ? resetZoom : undefined}
                                            title={(scale <= MIN_SCALE || scale >= MAX_SCALE) ? "Click to reset zoom" : ""}
                                        >
                                            {Math.round(scale * 100)}%
                                        </span>
                                        <button className='sd-icon-btn' onClick={zoomIn}>
                                            <Plus />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                        {/* PDF viewer End */}

                        {/* Solution Linked Section Start  */}
                        <div className={`shop-drawing-slide-menu overflow-auto h-100`} style={{ minWidth: "22rem" }}>
                            <div className='d-flex flex-column gap-2 m-2 ' style={{ height: "97%" }}>
                                <div className='flex-grow-1 overflow-auto'>
                                    <h4 className=''>
                                        GENERAL INFORMATION
                                    </h4>
                                    <div className="form-group">
                                        <label>
                                            LOCATION
                                        </label>
                                        <div>
                                            <MultiSelect
                                                className="sd-dark-multiselect"
                                                panelClassName='sd-dark-multiselect'
                                                filter
                                                style={{ width: '100%' }}
                                                value={selectedLocations}
                                                options={solutionDDL}
                                                optionLabel='solutionTitle'
                                                itemTemplate={(option: ProductionDrawingSolutionDDLEntity) => {
                                                    return truncateString(option.solutionTitle, 31);
                                                }}
                                                optionValue='uuid'
                                                maxSelectedLabels={0}
                                                onChange={(e) => setSelectedLocations(e.value)}
                                                placeholder="Select locations"
                                            />
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label>
                                            NOTES
                                        </label>
                                        <div>
                                            <InputTextarea className='sd-dark-textarea w-100' maxLength={180} value={selectedNotes} onChange={(e) => setSelectedNotes(e.target.value)} rows={5} cols={30} draggable={false} placeholder='Please enter notes' />
                                        </div>
                                    </div>
                                    <div>
                                        <button className='btn btn-primary mb-2 shop-drawing-btn-hover' disabled={IsNullOrEmptyArray(selectedLocations)} onClick={handleSaveLinkedSolutionAndNote}>Save</button>
                                    </div>

                                    {/* Solution Listing Start*/}
                                    <div className='d-flex flex-column gap-2'>
                                        {
                                            selectedPDF && selectedPDF.locations && !IsNullOrEmptyArray(selectedPDF.locations) && (
                                                handleFetchSelectedSolutions().map((item) => (
                                                    <LinkedSolutionList item={item} />
                                                ))
                                            )
                                        }
                                    </div>

                                </div>

                                <div>
                                    <button className='btn btn-primary w-100 mb-2 shop-drawing-btn-hover' disabled={handleGetConfirmedCount() !== PDFPageList.length} onClick={handleSubmitLinked}>Submit</button>
                                    <button className='btn btn-secondary w-100' onClick={() => {
                                        if (flowState && !IsStringNullEmptyOrUndefined(flowState.sourcePath) && !IsStringNullEmptyOrUndefined(flowState.workOrderId)) {
                                            navigate(flowState.sourcePath, {
                                                replace: true,
                                                state: {
                                                    ...flowState,
                                                    restoreProductionSelection: true
                                                }
                                            });
                                            return;
                                        }
                                        navigate(-1);
                                    }}>Back</button>
                                </div>
                                {/* Solution Listing End */}

                            </div>
                        </div>
                        {/* Solution Linked Section END  */}
                    </div>
                </div>
            </div >
        </>
    )
}

export default ProductionDrawingLink
